import React, { useEffect } from 'react';
import styles from './styles.module.scss';

// assets

// services

// props

// components
import { ActionButton, IDropdownOption, Link, Stack, TagPicker } from '@fluentui/react';
import { IVendorResourceShortProps } from '../../../../../props/data/vendors';
import Label from '../../../../typography/label';
import Text from '../../../../typography/text';
import VendorsService from '../../../../../services/data/vendors';
import VendorForm from '../../../../operationals/vendors/general/form';
import PermissionsService from '../../../../../services/permissions';
import { useStore } from '../../../../../stores/root';

type SelectVendorProps = {
    selected?: IVendorResourceShortProps;
    options?: IVendorResourceShortProps[];
    qs?: string[];
    label?: string;
    hideLabel?: boolean;
    disabled?: boolean;
    required?: boolean;
    allowCreate?: boolean;
    allowPending?: boolean;
    allowExpired?: boolean;
    errorMessage?: string;
    onChange(selected?: IVendorResourceShortProps): void;
}

const SelectVendor: React.FC<SelectVendorProps> = (props: SelectVendorProps) => {
    const { user, banner } = useStore();
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();
    const [selected, setSelected] = React.useState<IVendorResourceShortProps | undefined>();
    const isSelectedUnverified = selected && selected.verificationStatus !== 'verified' && selected.verificationStatus !== 'verified_with_risk';

    useEffect(() => {
        setSelected(props.selected);
    }, [props.selected]);

    const _onSearchVendor = async (keyword: string) => {
        try {
            if (props.options) {
                return props.options.map((item) => ({
                    key: item.id,
                    name: item.name,
                    metadata: item
                }));
            } else {
                const qs: string[] = props.qs ? [...props.qs] : [];
                if (keyword && keyword.trim() !== '') { qs.push(`search=${keyword}`) }
                if (!qs.some(q => q.startsWith('status='))) {
                    const statuses = ['verified', 'verified_with_risk'];
                    if (props.allowPending) statuses.push('pending_verification');
                    if (props.allowExpired) statuses.push('expired');
                    qs.push(`status=${statuses.join(';')}`);
                }

                const options = (await VendorsService.retrieve(qs.join("&"))).data.map((item) => ({
                    key: item.id,
                    name: item.name,
                    metadata: item
                }));

                return options;
            }
        } catch (error) {
            return [];
        }
    }

    return <Stack>
        {props.hideLabel ? null : <Label style={{padding: '3px 0px'}} size={'xsmall'} required={props.required}>{props.label || "Vendor"}</Label>}
        {!selected ? <>
            <TagPicker
                selectedItems={[]}
                removeButtonAriaLabel='Remove'
                itemLimit={1}
                onItemSelected={(item: any) => {
                    const selected = item ? item.metadata : undefined;
                    props.onChange(selected);

                    return null;
                }}
                onRenderSuggestionsItem={(props: any) => {
                    const md = props.metadata as IVendorResourceShortProps;

                    return <Stack styles={{ root: { padding: 5 }}} horizontalAlign='baseline'>
                        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                            <Label size={'small'}>{md.name}</Label>
                            {md.verificationStatus === 'pending_verification' ? (
                                <span style={{ fontSize: 11, color: '#b45309', backgroundColor: '#fef3c7', padding: '1px 6px', borderRadius: 4, fontWeight: 500 }}>
                                    Pending Verification
                                </span>
                            ) : md.verificationStatus === 'verified_with_risk' ? (
                                <span style={{ fontSize: 11, color: '#0369a1', backgroundColor: '#e0f2fe', padding: '1px 6px', borderRadius: 4, fontWeight: 500 }}>
                                    Verified with Risk
                                </span>
                            ) : md.verificationStatus === 'expired' ? (
                                <span style={{ fontSize: 11, color: '#dc2626', backgroundColor: '#fef2f2', padding: '1px 6px', borderRadius: 4, fontWeight: 500 }}>
                                    Expired Contract
                                </span>
                            ) : null}
                        </Stack>
                        {(md.npwp || "").trim() !== "" ? <Text size={'small'}>{md.npwp}</Text> : null}
                    </Stack>;
                }}
                onResolveSuggestions={_onSearchVendor}
                onEmptyResolveSuggestions={() => _onSearchVendor('')}
            />
            {props.allowCreate && PermissionsService.hasPermission(['vendors.create'], user.permissions) ? <Text size={'small'}>Cannot find vendor? <Link onClick={() => {setActiveSurface('create')}}>Click here</Link> to create vendor</Text> : null}
        </> : null}
        {props.errorMessage ? <Text style={{ marginTop: 3 }} size={'xsmall'} className={'color-red'}>{props.errorMessage}</Text> : null}
        {selected ? <Stack styles={{ root: { padding: 8 } }} className={'card'} horizontal tokens={{ childrenGap: 20 }} horizontalAlign={'space-between'} verticalAlign='center'>
            <Stack>
                <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                    <Label size={'small'}>{selected.name}</Label>
                    {selected.verificationStatus === 'pending_verification' ? (
                        <span style={{ fontSize: 11, color: '#b45309', backgroundColor: '#fef3c7', padding: '1px 6px', borderRadius: 4, fontWeight: 500 }}>
                            Pending Verification
                        </span>
                    ) : selected.verificationStatus === 'verified_with_risk' ? (
                        <span style={{ fontSize: 11, color: '#0369a1', backgroundColor: '#e0f2fe', padding: '1px 6px', borderRadius: 4, fontWeight: 500 }}>
                            Verified with Risk
                        </span>
                    ) : selected.verificationStatus === 'expired' ? (
                        <span style={{ fontSize: 11, color: '#dc2626', backgroundColor: '#fef2f2', padding: '1px 6px', borderRadius: 4, fontWeight: 500 }}>
                            Expired Contract
                        </span>
                    ) : null}
                </Stack>
                {(selected.npwp || "").trim() !== "" ? <Text size={'small'}>{selected.npwp}</Text> : null}
                {selected.phoneNumber ? <Text size={'small'}>{selected.phoneNumber}</Text> : null}
                {selected.address ? <Text size={'small'}>{selected.address}</Text> : null}
            </Stack>
            {!props.disabled ? <ActionButton iconProps={{ iconName: "Delete" }} onClick={() => { props.onChange(); }} /> : null}
        </Stack> : null}
        {activeSurface === 'create' ? <VendorForm onDismissed={(refresh, vendor) => {
            setActiveSurface(undefined);
            if (vendor) {
                if (vendor.verificationStatus === 'verified' || vendor.verificationStatus === 'verified_with_risk' || (props.allowPending && vendor.verificationStatus === 'pending_verification')) {
                    props.onChange(vendor);
                } else {
                    props.onChange(undefined);
                    banner.add({
                        key: 'vendor_created_pending',
                        variant: 'warning',
                        text: `Vendor "${vendor.name}" berhasil dibuat tetapi berstatus Pending Verification. Vendor harus diverifikasi sebelum dapat digunakan.`
                    });
                }
            }
        }} /> : null}
    </Stack>;
};

export default SelectVendor;
